import { FC, ReactElement } from "react";
import { Box, Typography } from "@mui/material";
import { IPrepaidMenuPackage } from "../../../helpers/prepaidMenuHelpers";

interface iPrepaidMenuSelectionsProps {
	packages: IPrepaidMenuPackage[];
	/** When set, truncates the plain-text equivalent for preview surfaces. */
	previewMaxLength?: number;
}

const splitCommaSeparatedValues = (value: string): string[] =>
	value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);

const PrepaidMenuSelections: FC<iPrepaidMenuSelectionsProps> = ({ packages, previewMaxLength }) => {
	const showPackageNames = packages.length > 1;
	let remainingChars = previewMaxLength;
	const rows: ReactElement[] = [];

	const canFit = (text: string): boolean => {
		if (remainingChars === undefined) {
			return true;
		}
		if (remainingChars <= 0) {
			return false;
		}
		remainingChars -= text.length + 1;
		return true;
	};

	for (const [packageIndex, menuPackage] of packages.entries()) {
		if (remainingChars !== undefined && remainingChars <= 0) {
			break;
		}

		if (showPackageNames && menuPackage.name) {
			if (!canFit(menuPackage.name)) {
				break;
			}

			rows.push(
				<Typography
					key={`pkg-${packageIndex}`}
					component="div"
					variant="inherit"
					sx={{
						fontWeight: 500,
						mt: packageIndex === 0 ? 0 : 1.5
					}}>
					{menuPackage.name}
				</Typography>,
			);
		}

		for (const [selectionIndex, selection] of menuPackage.selections.entries()) {
			const headingText = `${selection.name}:`;
			if (!canFit(headingText)) {
				break;
			}

			rows.push(
				<Typography
					key={`sel-heading-${packageIndex}-${selectionIndex}`}
					component="div"
					variant="inherit"
					sx={{
						fontWeight: 500,
						mt: rows.length === 0 ? 0 : 1
					}}>
					{selection.name}:
				</Typography>,
			);

			const values = splitCommaSeparatedValues(selection.value);
			for (const [valueIndex, value] of values.entries()) {
				const itemText = `• ${value}`;
				if (!canFit(itemText)) {
					break;
				}

				rows.push(
					<Typography
						key={`sel-item-${packageIndex}-${selectionIndex}-${valueIndex}`}
						component="div"
						variant="inherit"
						sx={{ whiteSpace: "pre-wrap" }}
					>
						• {value}
					</Typography>,
				);
			}
		}
	}

	return <Box>{rows}</Box>;
};

export default PrepaidMenuSelections;
